'use client';

import React, {
  forwardRef,
  useState,
  type CSSProperties,
  type ChangeEventHandler,
  type InputHTMLAttributes,
} from 'react';
import { useColors } from '../theme/ThemeProvider';
import { fontSize, radius, space, motion } from '../theme/tokens';

export interface TextInputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'size'> {
  value: string;
  onChange: ChangeEventHandler<HTMLInputElement>;
  type?: 'text' | 'password' | 'email' | 'number';
  style?: CSSProperties;
}

/**
 * Themed text input with visible border and focus state.
 * Border color strengthens on focus for clear affordance.
 */
export const TextInput = forwardRef<HTMLInputElement, TextInputProps>(
  function TextInput({ value, onChange, type = 'text', style, ...rest }, ref) {
    const colors = useColors();
    const [focused, setFocused] = useState(false);

    return (
      <input
        ref={ref}
        type={type}
        value={value}
        onChange={onChange}
        onFocus={e => {
          setFocused(true);
          rest.onFocus?.(e);
        }}
        onBlur={e => {
          setFocused(false);
          rest.onBlur?.(e);
        }}
        style={{
          width: '100%',
          height: 44,
          padding: `0 ${space[4]}px`,
          borderRadius: radius.sm,
          border: `1.5px solid ${focused ? colors.accent : colors.border}`,
          background: colors.surface,
          color: colors.text,
          fontSize: fontSize.md,
          boxSizing: 'border-box',
          outline: 'none',
          fontFamily: 'inherit',
          transition: `border-color ${motion.duration.fast}ms ${motion.easing.out}, background ${motion.duration.base}ms`,
          ...style,
        }}
        {...rest}
      />
    );
  }
);
